import { type SubmitEvent, useRef } from 'react';

import { Button } from '#/components/Button/Button';
import { InputClearButtonSuffix } from '#/components/Input/affixes/InputClearButtonSuffix';
import { Input } from '#/components/Input/Input';
import { LocationInput } from '#/components/LocationInput/LocationInput';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { SearchLocation } from '#/modules/search/types/SearchLocation';

import {
  EmbedSearchWidgetLayoutSearchDesktopButtonHolder,
  EmbedSearchWidgetLayoutSearchFormEl,
  EmbedSearchWidgetLayoutSearchLocationHolder,
  EmbedSearchWidgetLayoutSearchMobileButtonHolder,
  EmbedSearchWidgetLayoutSearchQueryInputMaybeButtonHolder,
} from './EmbedSearchWidgetLayoutSearch.styled';

type Props = {
  initialQuery: string;
  onSearch: (data: {
    query: string;
    triggeredByRemoteLocationChange: boolean;
  }) => void;
  searchOnClear: boolean;
  searchLocation: SearchLocation | undefined;
  onChangeSearchLocation: (
    newSearchLocation: SearchLocation | undefined,
  ) => boolean;
};

export function EmbedSearchWidgetLayoutSearch({
  initialQuery,
  onSearch,
  searchOnClear,
  searchLocation,
  onChangeSearchLocation,
}: Props) {
  const { i18n } = useI18n();

  const queryInputRef = useRef<HTMLInputElement>(null);

  const triggerSearch = ({
    triggeredByRemoteLocationChange,
  }: {
    triggeredByRemoteLocationChange: boolean;
  }) => {
    onSearch({
      query: queryInputRef.current?.value ?? '',
      triggeredByRemoteLocationChange,
    });
  };

  const clearInput = () => {
    const queryInput = queryInputRef.current;
    if (queryInput) queryInput.value = '';

    if (searchOnClear)
      triggerSearch({ triggeredByRemoteLocationChange: false });
  };

  const submitForm = (event: SubmitEvent) => {
    event.preventDefault();
    triggerSearch({ triggeredByRemoteLocationChange: false });
  };

  return (
    <EmbedSearchWidgetLayoutSearchFormEl
      onSubmit={submitForm}
      data-qa-id="page-header-search-menu"
    >
      <EmbedSearchWidgetLayoutSearchQueryInputMaybeButtonHolder>
        <Input
          key={initialQuery}
          inputProps={{
            ref: queryInputRef,
            name: 'q',
            placeholder: i18n('Search by keyword, skill, or interest'),
            defaultValue: initialQuery,
            qaId: 'search-input',
            autoComplete: 'off',
          }}
          suffix={
            <InputClearButtonSuffix
              ariaLabel="Clear search input"
              onClear={clearInput}
              qaId="search-input-clear"
            />
          }
          hideSuffixWhenEmpty
          styleVariant="contrast"
          affixVariant="transparent"
          fullWidth
        />

        <EmbedSearchWidgetLayoutSearchMobileButtonHolder>
          <Button
            type="submit"
            variant="primary"
            icon={{ name: 'search' }}
            qaId="mobile-search-button"
            width={106}
            height={48}
            fullWidth
          >
            {i18n('Search')}
          </Button>
        </EmbedSearchWidgetLayoutSearchMobileButtonHolder>
      </EmbedSearchWidgetLayoutSearchQueryInputMaybeButtonHolder>

      <EmbedSearchWidgetLayoutSearchLocationHolder>
        <LocationInput
          affixVariant="transparent"
          styleVariant="contrast"
          hasRemoteOptions
          showLocationIcon
          showClearButton
          placeholder={i18n('Everywhere')}
          locationText={searchLocation?.text}
          id="page-header-desktop-search-location"
          onChange={(newLocation) => {
            // Avoid submitting twice - the LocationInput has a bug that triggers the onChange twice
            if (!onChangeSearchLocation(newLocation)) return;

            // Trigger search when search location changes
            triggerSearch({
              triggeredByRemoteLocationChange: Boolean(newLocation?.isRemote),
            });
          }}
        />
      </EmbedSearchWidgetLayoutSearchLocationHolder>

      <EmbedSearchWidgetLayoutSearchDesktopButtonHolder>
        <Button
          type="submit"
          variant="primary"
          icon={{ name: 'search' }}
          qaId="search-button"
          height={48}
          fullWidth
        >
          {i18n('Search')}
        </Button>
      </EmbedSearchWidgetLayoutSearchDesktopButtonHolder>
    </EmbedSearchWidgetLayoutSearchFormEl>
  );
}
